/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { getBatchService } from 'src/core/services/batchService';
import {
    AmlProjectStatus,
    BatchCallRequest,
    BatchResponse,
    CreateProjectRequestContent,
    DataAssetsRequestContent,
    DataAssetsResponse,
    DataStoreRequestContent,
    exportLabelStatus,
    GetAmlWorkspaceInfo,
    getInstructionsResponse,
    getProjectNameResponse,
    ImportLabelObject,
    SwitchLabelsStatus,
    terminateProjectResponse
} from 'src/core/types/azureMachineLearningTypes';
import { generateEntityColorFromString } from 'src/core/utils/color';
import { CustomTextClassificationStore } from 'src/customText/core/stores/CustomTextClassificationStore';
import { CustomTextExtractionStore } from 'src/customText/core/stores/CustomTextExtractionStore';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';

/**
 * Represents a service for integration
 * with Azure Machine Learning.
 */
export const getAmlService = () => {
    const batchService = getBatchService();

    const constructAmlProjectObject = async ({
        amlConnectionInfo,
        datasetId,
        extractionStore,
        classificationStore,
        projectKind,
        region,
        armToken,
        resourceEndPoint,
        languageStudioProjectName
    }: {
        amlConnectionInfo: AMLConnectionInfo;
        datasetId: string;
        extractionStore: CustomTextExtractionStore;
        classificationStore: CustomTextClassificationStore;
        projectKind: string;
        region: string;
        armToken: string;
        resourceEndPoint: string;
        languageStudioProjectName: string;
    }) => {
        const { vendor, projectName, labelingInstructionText, labelingInstructionUrl, projectRoute } = amlConnectionInfo;
        const headers = { authorization: `Bearer ${armToken}` };
        let content: CreateProjectRequestContent = {
            name: projectName,
            labelNames: [],
            enableIncrementalDataset: true,
            vendorConfig: { vendorName: vendor },
            datasetVersion: '1',
            instructions: labelingInstructionText,
            detailedInstructionsUri: labelingInstructionUrl,
            version: { major: 1, minor: 0 },
            generalSettingsConfig: { showDatapointSource: true, showLabelTimer: true },
            labelCategories: {},
            datasetId
        };
        if (projectKind === 'CustomEntityRecognition') {
            content = { ...content, type: 'Text Named Entity Recognition' };
            extractionStore.entities.map((item, index) => {
                const { name } = item;
                content.labelCategories[name] = {
                    displayName: item.name,
                    labelSettings: { color: generateEntityColorFromString(item.name) },
                    sequenceNumber: index,
                    allowMultiSelect: false,
                    labelClasses: {}
                };
            });
            content.languageStudioMetadata = {
                languageStudioProjectURL: `https://language.cognitive.azure.com/customText/projects/extraction/${languageStudioProjectName}/tag`,
                languageStudioProjectName,
                languageStudioResourceId: resourceEndPoint
            };
        } else if (projectKind === 'CustomSingleLabelClassification') {
            content = { ...content, type: 'Text Classification Multi-class' };
            classificationStore.classes.map((item, index) => {
                const { name } = item;
                content.labelCategories[name] = {
                    displayName: item.name,
                    labelSettings: { color: generateEntityColorFromString(item.name) },
                    sequenceNumber: index,
                    allowMultiSelect: false,
                    labelClasses: {}
                };
            });
            content.languageStudioMetadata = {
                languageStudioProjectURL: `https://language.cognitive.azure.com/customText/projects/classification/${languageStudioProjectName}`,
                languageStudioProjectName,
                languageStudioResourceId: resourceEndPoint
            };
        } else {
            content = { ...content, type: 'Text Classification Multi-label' };
            classificationStore.classes.map((item, index) => {
                const { name } = item;
                content.labelCategories[name] = {
                    displayName: item.name,
                    labelSettings: { color: generateEntityColorFromString(item.name) },
                    sequenceNumber: index,
                    allowMultiSelect: false,
                    labelClasses: {}
                };
            });
            content.languageStudioMetadata = {
                languageStudioProjectURL: `https://language.cognitive.azure.com/customText/projects/classification/${languageStudioProjectName}`,
                languageStudioProjectName,
                languageStudioResourceId: resourceEndPoint
            };
        }
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${projectRoute}/projects`;

        const projectResponse = await batchService.createSinglePost<CreateProjectRequestContent, string>({ content, requestUrl, headers });
        return projectResponse;
    };

    const constructAmlDataStoreObject = async ({
        amlConnectionInfo,
        dataStoreName,
        armToken,
        region
    }: {
        amlConnectionInfo: AMLConnectionInfo;
        dataStoreName: string;
        armToken: string;
        region: string;
    }) => {
        const { subscriptionId, projectRoute, storageAccount, storageResourceGroup: resourceGroup, containerName } = amlConnectionInfo;
        const headers = { authorization: `Bearer ${armToken}` };

        const content: DataStoreRequestContent = {
            azureStorageSection: {
                accountName: storageAccount,
                containerName,
                credentialType: 'None',
                resourceGroup,
                serviceDataAccessAuthIdentity: 'WorkspaceSystemAssignedIdentity',
                subscriptionId
            },
            dataStoreType: 'AzureBlob',
            name: dataStoreName
        };
        const requestUrl = `https://ml.azure.com/api/${region}/datastore/v1.0${projectRoute}/datastores?skipValidation=true`;

        await batchService.createSinglePost({ content, requestUrl, headers });
    };

    const constructAmlDataAssetsObject = async ({
        amlConnectionInfo,
        dataAssetsName,
        dataStoreName,
        armToken,
        region
    }: {
        amlConnectionInfo: AMLConnectionInfo;
        dataAssetsName: string;
        dataStoreName: string;
        armToken: string;
        region: String;
    }) => {
        const { projectRoute } = amlConnectionInfo;
        const headers = { authorization: `Bearer ${armToken}` };
        const content: DataAssetsRequestContent = {
            dataPath: {
                dataStoreName,
                partitionFormatIgnoreError: false,
                relativePath: '*',
                sqlDataPath: {}
            },
            datasetType: 'file',
            general: {
                properties: {}
            },
            sourcePropertiesOverride: {
                dataSourceType: 'dne',
                delimiter: 'n'
            },
            isVisible: true,
            name: dataAssetsName
        };
        const requestUrl = `https://ml.azure.com/api/${region}/dataset/v1.0${projectRoute}/datasets/register?registerAsPending=false&ifExistsOK=true&updateDefinitionIfExists=true&skipValidation=true`;

        const response = await batchService.createSinglePost<DataAssetsRequestContent, DataAssetsResponse>({
            content,
            requestUrl,
            headers
        });
        return response;
    };

    const constructAmlProjectMetadataObject = ({
        projectRoute,
        update = false,
        isLabelingLocked,
        amlWorkspaceContainerName
    }: {
        projectRoute: string;
        update?: boolean;
        isLabelingLocked?: boolean;
        amlWorkspaceContainerName?: string;
    }) => {
        let requestObject;
        if (update) {
            requestObject = {
                isLabelingLocked
            };
        } else {
            requestObject = {
                amlContainerName: amlWorkspaceContainerName,
                isLabelingLocked: false,
                AmlProjectPath: projectRoute
            };
        }

        return requestObject;
    };

    const constructGetProjectNameObject = async ({
        armToken,
        region,
        amlProjectPath
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}`;

        const projectResponse = await batchService.createSingleGet<BatchCallRequest<null>, getProjectNameResponse>({ requestUrl, headers });
        return projectResponse;
    };

    const constructGetAmlWorkspaceInfo = async ({
        armToken,
        region,
        amlProjectPath
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const requestUrl = `https://ml.azure.com/api/${region}/datastore/v1.0${amlProjectPath}/default`;

        const projectResponse = await batchService.createSingleGet<BatchCallRequest<null>, GetAmlWorkspaceInfo>({ requestUrl, headers });
        return projectResponse;
    };

    const constructGetInstructionsObject = async ({
        armToken,
        region,
        amlProjectPath
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/instructions`;

        const instructionsResponse = await batchService.createSingleGet<null, getInstructionsResponse>({ requestUrl, headers });
        return instructionsResponse;
    };

    const constructValidateProjectNameObject = async ({
        armToken,
        region,
        projectRoute,
        projectName
    }: {
        armToken: string;
        region: string;
        projectRoute: string;
        projectName: string;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const content = projectName;
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${projectRoute}/projects/validate`;

        await batchService.createSinglePost({ content, requestUrl, headers });
    };

    const constructImportLabelsFromAmlObject = async ({
        region,
        armToken,
        amlProjectPath,
        amlConnectionInfo,
        operationHandle
    }: {
        region: String;
        armToken: string;
        amlProjectPath: string;
        amlConnectionInfo: AMLConnectionInfo;
        operationHandle: OperationHandler<BatchResponse<exportLabelStatus>[]>;
    }) => {
        const { datasetId } = amlConnectionInfo;
        const headers = { authorization: `Bearer ${armToken}` };

        const content = {
            format: 'Dataset',
            exportOptions: {
                labeler: true,
                labelingTimeInSeconds: true,
                updatedBy: true,
                labeledDatapointTypes: ['LabeledDatapoint'],
                coordinateType: 'Normalized'
            }
        };
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/datasets/${datasetId}/export`;

        const { content: exportId } = await batchService.createSinglePost<unknown, string>({ content, requestUrl, headers });

        const getRequestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/datasets/${datasetId}/export/${exportId}/status`;

        const response = await batchService.batchLongOperations({ headers, requestUrl: getRequestUrl, operationHandle });

        const importLabelObject: ImportLabelObject = {
            labelsDirectoryName: response?.content?.exportRequestId
        };

        return importLabelObject;
    };

    const getAmlProjectStatus = async ({
        armToken,
        region,
        amlProjectPath,
        operationHandle
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
        operationHandle: OperationHandler<BatchResponse<AmlProjectStatus>[]>;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/summary`;
        const response = await batchService.batchLongOperations({ headers, requestUrl, operationHandle });
        return response.content.state;
    };

    const terminateAmlConnection = async ({
        armToken,
        region,
        amlProjectPath
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
    }) => {
        const headers = { authorization: `Bearer ${armToken}` };
        const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/languageStudioTransition/LanguageStudio/terminateConnection`;
        const deleteResponse = await batchService.createSinglePatch<null, terminateProjectResponse>({ requestUrl, headers });
        return deleteResponse;
    };

    const switchLabelToAml = async ({
        armToken,
        region,
        amlProjectPath,
        labels,
        operationHandle
    }: {
        armToken: string;
        region: string;
        amlProjectPath: string;
        labels: string;
        operationHandle: OperationHandler<BatchResponse<SwitchLabelsStatus>[]>;
    }) => {
        try {
            const headers = { authorization: `Bearer ${armToken}` };
            const requestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/languageStudioTransition/labelImport`;
            const content = labels;
            const { content: importId } = await batchService.createSinglePost({ headers, requestUrl, content });

            const getRequestUrl = `https://ml.azure.com/api/${region}/labeling-api/v1.0${amlProjectPath}/labelImports/${importId}/status`;

            const response = await batchService.batchLongOperations({ headers, requestUrl: getRequestUrl, operationHandle });

            return response.content;
        } catch (error) {
            throw error;
        }
    };

    return {
        switchLabelToAml,
        getAmlProjectStatus,
        terminateAmlConnection,
        constructAmlProjectObject,
        constructAmlDataStoreObject,
        constructAmlDataAssetsObject,
        constructGetAmlWorkspaceInfo,
        constructGetProjectNameObject,
        constructGetInstructionsObject,
        constructImportLabelsFromAmlObject,
        constructAmlProjectMetadataObject,
        constructValidateProjectNameObject
    };
};
